import { HOTPADS_COMSCORE_WINDOW_PARAMS_KEY } from './constants';
import { getHotpadsComscoreParams } from './params';
import { getIdentitySignalBootstrapScriptContents } from '@/src/lib/identity/browserIdentity';

type HotpadsComscoreReadySignal = {
  ready: Promise<void>;
  resolve: () => void;
};

type HotpadsComscoreWindow = Window & {
  __hotpadsComscoreReady?: HotpadsComscoreReadySignal;
};

function createComscoreReadySignal(): HotpadsComscoreReadySignal {
  let settle: () => void = () => undefined;
  let settled = false;
  const ready = new Promise<void>((resolve) => {
    settle = resolve;
  });

  return {
    ready,
    resolve() {
      if (settled) return;
      settled = true;
      settle();
    },
  };
}

export function getHotpadsComscoreReady(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const comscoreWindow = window as HotpadsComscoreWindow;
  comscoreWindow.__hotpadsComscoreReady ??= createComscoreReadySignal();
  return comscoreWindow.__hotpadsComscoreReady.ready;
}

function serializeForInlineScript(value: unknown) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function getComscoreReadyBootstrapScriptContents() {
  return `
    if (!window.__hotpadsComscoreReady) {
      let settleComscore;
      let comscoreSettled = false;
      window.__hotpadsComscoreReady = {
        ready: new Promise(function(resolve) { settleComscore = resolve; }),
        resolve: function() {
          if (comscoreSettled) return;
          comscoreSettled = true;
          settleComscore();
        }
      };
    }
  `;
}

export function getHotpadsComscoreScriptContents() {
  const params = serializeForInlineScript(getHotpadsComscoreParams({}));

  return `
    if (typeof window !== 'undefined') {
      ${getIdentitySignalBootstrapScriptContents()}
      ${getComscoreReadyBootstrapScriptContents()}
      window.__hotpadsIdentity.ready.then(function(identity) {
        const params = ${params};
        params.cs_fpid = identity.userToken;
        params.cs_fpit = identity.status === 'authenticated' ? 'li' : 'lo';
        window.${HOTPADS_COMSCORE_WINDOW_PARAMS_KEY} = params;

        if (!window._comscore) {
          window._comscore = [];
          window._comscore.push(params);
        }
      });
    }
  `;
}

export function getHotpadsComscoreBeaconScriptContents(beaconSrc: string) {
  return `
    ${getIdentitySignalBootstrapScriptContents()}
    ${getComscoreReadyBootstrapScriptContents()}
    window.__hotpadsIdentity.ready.then(function() {
      if (document.getElementById('comscore-beacon-runtime')) return;
      const script = document.createElement('script');
      script.id = 'comscore-beacon-runtime';
      script.async = true;
      script.src = ${serializeForInlineScript(beaconSrc)};
      script.onload = function() { window.__hotpadsComscoreReady?.resolve(); };
      document.head.appendChild(script);
    });
  `;
}
